import localforage from 'localforage';
import {
  uploadToGoogleDrive,
  findOrCreateGoogleDriveFolder
} from '../utils/googleDrive';
import {
  COMPANY_KEY,
  CLIENTS_KEY,
  PRODUCTS_KEY,
  INVOICES_KEY,
  INVOICE_DETAILS,
  CLIENT_FORM_KEY,
  PRODUCT_FORM_KEY,
  INVOICE_FORM_KEY,
  DEFAULT_INVOICE_COLOR,
  DEFAULT_INVOICE_BG,
  QUOTATIONS_KEY,
  QUOTATION_DETAILS,
  QUOTATION_FORM_KEY,
  DEFAULT_QUOTATION_COLOR,
  DEFAULT_QUOTATION_BG,
} from '../constants/localKeys';
import secureLogger from '../utils/secureLogger';
import { mergeRowsById } from '../utils/syncMeta';
import {
  ASSETS_KEY,
  isAssetKey,
  mergeIncomingAssets,
  invalidateAssetsCache,
  setItem as compactingSetItem,
} from './assetStorage';
import {
  clearGoogleAuth,
  getStoredGoogleAuth,
  persistGoogleAuth,
  isStoredTokenExpiringSoon,
  requestTokenRefresh,
} from './googleTokenService';

// Cloud sync configuration
const SYNC_CONFIG = {
  APP_FOLDER_NAME: 'Logislip_Data',
  BACKUP_FILE_NAME: 'logislip_backup.json',
  BACKUP_PREVIOUS_FILE_NAME: 'logislip_backup.previous.json',
  METADATA_FILE_NAME: 'sync_metadata.json',
  AUTO_SYNC_INTERVAL: 5 * 60 * 1000, // 5 minutes
  MAX_RETRIES: 3,
  RETRY_DELAY: 2000,
  VERSION_HISTORY_LIMIT: 10,
  BACKUP_ROTATION_INTERVAL_MS: 24 * 60 * 60 * 1000, // Rotate at most once per day
  // Cold-storage / archive settings
  ARCHIVE_FOLDER_NAME: 'cold_storage',
  ARCHIVE_FILE_NAME: 'archived_invoices.json',
  ARCHIVE_THRESHOLD_DAYS: 90, // Paid invoices older than this are archived
  // Versioned-snapshot settings (AWS RDS–style retention)
  //   - Automated snapshots are server-copied daily into VERSIONS_FOLDER_NAME
  //     with a timestamped name. The oldest are pruned beyond retention.
  //   - Manual snapshots are never pruned (they carry a distinct prefix).
  VERSIONS_FOLDER_NAME: 'versions',
  VERSION_AUTO_PREFIX: 'logislip_backup_auto_',
  VERSION_MANUAL_PREFIX: 'logislip_backup_manual_',
  VERSION_AUTO_RETENTION: 7,   // keep last 7 automated snapshots
};

// Dedicated localforage key used to persist sync coordination state
// (dirtyKeys + syncQueue) across reloads. Excluded from SYNC_KEYS.
const SYNC_STATE_KEY = '__cloudSync_state';

// Hash placeholder used while computing the embedded _metadata.hash so
// the field exists during hashing and verifies cleanly on download.
const HASH_PLACEHOLDER = '__pending__';

// Schema version stamped into every uploaded backup. Bump only when the
// on-disk shape changes in a way that needs migration on read.
// Newer-than-current blobs are refused to avoid an old client overwriting
// fresh data with a degraded copy. Older blobs are run through
// migrateCloudData() before applying.
const CURRENT_SCHEMA_VERSION = 1;

// localStorage key used to detect when a different Google account signs in
// on the same device. On switch we clear the local sync caches to prevent
// one user's data from being uploaded under another user's Drive folder.
const LAST_USER_KEY = 'cloudSync_lastUserId';

// Data keys that need to be synced
const SYNC_KEYS = [
  COMPANY_KEY,
  CLIENTS_KEY,
  PRODUCTS_KEY,
  INVOICES_KEY,
  INVOICE_DETAILS,
  CLIENT_FORM_KEY,
  PRODUCT_FORM_KEY,
  INVOICE_FORM_KEY,
  DEFAULT_INVOICE_COLOR,
  DEFAULT_INVOICE_BG,
  QUOTATIONS_KEY,
  QUOTATION_DETAILS,
  QUOTATION_FORM_KEY,
  DEFAULT_QUOTATION_COLOR,
  DEFAULT_QUOTATION_BG,
];

// Keys whose value is an array of rows with stable `id`. These get per-row
// merge by updatedAt on cloud apply (instead of full-array replace) so that
// concurrent edits on different devices don't clobber each other. Rows
// carry tombstones (_deleted: true) when soft-deleted — see utils/syncMeta.
const MERGEABLE_ARRAY_KEYS = new Set([
  CLIENTS_KEY,
  PRODUCTS_KEY,
  INVOICES_KEY,
  INVOICE_DETAILS,
  QUOTATIONS_KEY,
  QUOTATION_DETAILS,
]);

class CloudSyncService {
  constructor() {
    this.accessToken = null;
    this.userId = null;
    this.appFolderId = null;
    this.isOnline = navigator.onLine;
    this.syncQueue = [];
    this.lastSyncTime = null;
    this.syncInProgress = false;
    this.autoSyncInterval = null;
    this.eventListeners = new Set();
    this.dataSyncTimeout = null;

    // Incremental sync tracking
    this.dirtyKeys = new Set();        // Keys modified since last successful sync
    this.backupFileId = null;          // Cached Google Drive file ID (skip search on every sync)
    this.previousBackupFileId = null;  // Cached previous-generation backup file ID
    // Re-entrancy counter (replaces a boolean): incremented around every cloud
    // apply, decremented in finally. Hooks ignore writes while > 0. A counter
    // is needed because two applies can interleave (e.g. initial restore +
    // auto-sync arriving together) and a boolean would flip false too early.
    this.cloudApplyDepth = 0;

    // Cold-storage / archive state
    this.archiveFolderId = null;       // Cached cold_storage/ folder ID
    this.archiveFileId = null;         // Cached archived_invoices.json file ID

    // Versioned-snapshot state
    this.versionsFolderId = null;      // Cached versions/ folder ID

    // Initialize sync metadata in localStorage
    this.initializeSyncMetadata();

    // Restore in-flight sync state (dirtyKeys + queue) from localforage
    this.restoreSyncState();

    // Try to restore previous authentication state
    this.restoreAuthState();

    // Setup online/offline detection
    this.setupNetworkListeners();

    // Setup periodic sync
    this.setupAutoSync();

    // CloudSyncService initialized. Dirty-tracking is driven by explicit
    // calls from syncedStorage → triggerSync(); the old localforage monkey-
    // patch was removed in Tier 3c.
  }

  // Restore authentication state from localStorage
  async restoreAuthState() {
    try {
      const authState = getStoredGoogleAuth();
      if (authState) {
        const { accessToken, userId } = authState;
        
        if (accessToken && userId) {
          // Check if we have network connectivity before attempting to initialize
          if (!navigator.onLine) {
            secureLogger.debug('Device is offline, skipping cloud sync initialization');
            return false;
          }
          
          await this.initialize(accessToken, userId);
          return true;
        }
      }
    } catch (error) {
      secureLogger.debug('Cloud sync not available or not configured:', { hasAuth: !!getStoredGoogleAuth() });
      // Clear invalid auth state to prevent repeated failures
      clearGoogleAuth();
    }
    return false;
  }

  // Store authentication state in localStorage
  async storeAuthState(accessToken, userId) {
    try {
      persistGoogleAuth(accessToken, userId);
    } catch (error) {
      secureLogger.error('Failed to persist authentication state:', error);
    }
  }

  // Initialize the service
  async initialize(accessToken, userId) {
    try {
      // Validate access token format
      if (!accessToken || !accessToken.startsWith('ya29.')) {
        secureLogger.debug('Access token does not appear to be a valid Google token');
        return false;
      }
      
      // Check network connectivity
      if (!navigator.onLine) {
        secureLogger.debug('Device is offline, cannot initialize cloud sync');
        return false;
      }
      
      this.accessToken = accessToken;
      this.userId = userId;

      // If a different Google account signed in on this device, wipe the
      // local sync caches before touching Drive. Prevents the previous
      // user's IndexedDB data from being uploaded into the new user's
      // backup file (or vice-versa, on first sync after switch).
      await this._handleUserSwitch(userId);

      // Create or find app folder
      await this.setupAppFolder();

      // Persist authentication state
      await this.storeAuthState(accessToken, userId);

      // Perform initial sync (non-blocking)
      this.performSync('initialize').catch(error => {
        secureLogger.debug('Initial sync failed, continuing without cloud sync:', { message: error.message });
      });

      // Notify listeners
      this.notifyListeners('initialized', { success: true });

      return true;
    } catch (error) {
      secureLogger.debug('Failed to initialize Cloud Sync Service (continuing without cloud sync):', { 
        message: error.message,
        hasToken: !!accessToken,
        isOnline: navigator.onLine 
      });
      this.clearAuthenticationState();
      this.notifyListeners('initialized', { success: false, error });
      return false;
    }
  }

  // Setup app folder in Google Drive
  async setupAppFolder() {
    try {
      // const metadata = this.getSyncMetadata();
      // const deviceId = metadata.deviceId || this.generateDeviceId();
      
      // Use simpler folder name without device ID to avoid duplicates
      // Each user will have one main folder, and we'll use file versioning for conflict resolution
      const userFolderName = `${SYNC_CONFIG.APP_FOLDER_NAME}_${this.userId}`;
      
      const folder = await findOrCreateGoogleDriveFolder({
        accessToken: this.accessToken,
        folderName: userFolderName
      });
      
      this.appFolderId = folder.id;
    } catch (error) {
      secureLogger.error('Failed to setup app folder:', error);
      throw error;
    }
  }

  // Lightweight metadata-only check — no file content downloaded
  // Returns { id, modifiedTime, size } or null
  async getCloudFileMetadata() {
    try {
      if (!this.accessToken || !this.appFolderId) return null;
      if (!navigator.onLine) return null;

      // Fast path: direct lookup using cached fileId (1 API call, no search)
      const cachedId = this.backupFileId || this.getSyncMetadata().lastUploadFileId;
      if (cachedId) {
        const url = `https://www.googleapis.com/drive/v3/files/${cachedId}?fields=id,modifiedTime,size`;
        const response = await fetch(url, {
          headers: { Authorization: `Bearer ${this.accessToken}` }
        });
        if (response.ok) {
          const meta = await response.json();
          this.backupFileId = meta.id;
          return meta;
        }
        // File deleted or error — clear cache, fall through to search
        if (response.status === 404) {
          this.backupFileId = null;
          this.updateSyncMetadata({ lastUploadFileId: null });
        }
        if (response.status === 401) {
          this.clearAuthenticationState();
          return null;
        }
      }

      // Slow path: search by name (metadata only, no content)
      const query = `name='${SYNC_CONFIG.BACKUP_FILE_NAME}' and '${this.appFolderId}' in parents and trashed=false`;
      const searchUrl = `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(query)}&fields=files(id,modifiedTime,size)&orderBy=modifiedTime desc&pageSize=1`;
      const searchResponse = await fetch(searchUrl, {
        headers: { Authorization: `Bearer ${this.accessToken}` }
      });

      if (!searchResponse.ok) {
        if (searchResponse.status === 401) {
          this.clearAuthenticationState();
        }
        return null;
      }

      const searchData = await searchResponse.json();
      if (searchData.files.length === 0) return null;

      const file = searchData.files[0];
      this.backupFileId = file.id;
      return file;
    } catch (error) {
      secureLogger.debug('Failed to get cloud file metadata:', { message: error.message });
      return null;
    }
  }

  // Get all local data for sync
  async getAllLocalData() {
    const data = {};
    const metadata = this.getSyncMetadata();

    // Image-carrying keys already store the compact {$ref:"<hash>"} shape
    // in IDB (see services/assetStorage). Read them raw — the blob ships
    // to Drive in compact form, and the paired _assets map (added below)
    // lets the downloader rehydrate on apply.
    for (const key of SYNC_KEYS) {
      try {
        const value = await localforage.getItem(key);
        // Convert null to undefined to avoid DataCloneError
        data[key] = value === null ? undefined : value;
      } catch (error) {
        secureLogger.warn(`Failed to read ${key}:`, error);
        data[key] = undefined;
      }
    }

    try {
      const assets = await localforage.getItem(ASSETS_KEY);
      if (assets && typeof assets === 'object') {
        data._assets = assets;
      }
    } catch (error) {
      secureLogger.warn('Failed to read _assets for upload:', error);
    }

    const now = Date.now();
    data._metadata = {
      timestamp: now,
      deviceId: metadata.deviceId,
      version: metadata.syncVersion,
      schemaVersion: CURRENT_SCHEMA_VERSION,
      hash: await this.generateDataHash(data),
    };

    return data;
  }

  // Migrate a cloud blob from an older schema version forward.
  // No-op for v1 (current). When CURRENT_SCHEMA_VERSION is bumped, add
  // case branches here to translate older shapes.
  // Always returns a NEW object — never mutate the input.
  migrateCloudData(cloudData, fromVersion) {
    if (fromVersion === CURRENT_SCHEMA_VERSION) return cloudData;
    secureLogger.info(
      `Migrating cloud data from schema v${fromVersion} -> v${CURRENT_SCHEMA_VERSION}`
    );
    // No migrations defined yet. Return as-is and stamp the new version
    // so the next upload writes the current schema.
    const migrated = { ...cloudData };
    if (migrated._metadata) {
      migrated._metadata = { ...migrated._metadata, schemaVersion: CURRENT_SCHEMA_VERSION };
    }
    return migrated;
  }

  // SHA-256 hex digest over a stable JSON serialization.
  // Why: the previous 32-bit string hash collided easily as data grew,
  // causing the "no change, skip sync" fast path to drop real edits.
  async generateDataHash(data) {
    const jsonString = JSON.stringify(data, Object.keys(data).sort());
    if (typeof crypto !== 'undefined' && crypto.subtle) {
      const buffer = new TextEncoder().encode(jsonString);
      const digest = await crypto.subtle.digest('SHA-256', buffer);
      const bytes = new Uint8Array(digest);
      let hex = '';
      for (let i = 0; i < bytes.length; i++) {
        hex += bytes[i].toString(16).padStart(2, '0');
      }
      return hex;
    }
    // Fallback for non-secure contexts: keep behavior, just don't crash.
    let hash = 0;
    for (let i = 0; i < jsonString.length; i++) {
      const char = jsonString.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return hash.toString();
  }

  // Upload data to Google Drive
  // Uses cached fileId to skip the search API call on subsequent syncs.
  // Recomputes _metadata.hash AFTER all blob modifications (_syncInfo,
  // _archivedInvoiceIds added by the partitioner) so verifyDataHash
  // succeeds on download.
  async uploadToCloud(data, syncType = 'manual') {
    try {
      // Rotate the previous backup once per BACKUP_ROTATION_INTERVAL_MS
      // so we always have one prior generation to recover from.
      // Failures are non-fatal — the main upload still proceeds.
      try {
        await this.rotateBackupIfDue();
      } catch (rotErr) {
        secureLogger.warn('Backup rotation skipped:', rotErr);
      }

      const backupData = {
        ...data,
        _syncInfo: {
          syncType,
          timestamp: Date.now(),
          deviceId: this.getSyncMetadata().deviceId,
        }
      };

      // Recompute the integrity hash over the FINAL upload blob.
      // Without this, the embedded hash from getAllLocalData wouldn't cover
      // _syncInfo / _archivedInvoiceIds and verifyDataHash would always fail
      // on download.
      if (backupData._metadata) {
        const placeholderMeta = { ...backupData._metadata, hash: HASH_PLACEHOLDER };
        const hashable = { ...backupData, _metadata: placeholderMeta };
        const finalHash = await this.generateDataHash(hashable);
        backupData._metadata = { ...backupData._metadata, hash: finalHash };
      }

      const blob = new Blob([JSON.stringify(backupData, null, 2)], {
        type: 'application/json'
      });

      const fileName = SYNC_CONFIG.BACKUP_FILE_NAME;

      // Use cached fileId — only search if not cached
      let existingFileId = this.backupFileId;
      if (!existingFileId) {
        try {
          const searchQuery = `name='${fileName}' and '${this.appFolderId}' in parents and trashed=false`;
          const searchUrl = `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(searchQuery)}&fields=files(id,name)`;

          const searchResponse = await fetch(searchUrl, {
            headers: { Authorization: `Bearer ${this.accessToken}` }
          });

          if (searchResponse.ok) {
            const searchData = await searchResponse.json();
            if (searchData.files.length > 0) {
              existingFileId = searchData.files[0].id;
              this.backupFileId = existingFileId;
            }
          }
        } catch (error) {
          secureLogger.warn('Could not search for existing file:', error);
        }
      }

      let result;
      if (existingFileId) {
        // Update existing file — request modifiedTime in response
        const updateUrl = `https://www.googleapis.com/upload/drive/v3/files/${existingFileId}?uploadType=media&fields=id,modifiedTime`;
        const updateResponse = await fetch(updateUrl, {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${this.accessToken}`,
            'Content-Type': 'application/json',
          },
          body: blob,
        });

        if (!updateResponse.ok) {
          // If file was deleted, clear cache and fall through to create
          if (updateResponse.status === 404) {
            this.backupFileId = null;
            existingFileId = null;
          } else {
            throw new Error('Failed to update existing file: ' + (await updateResponse.text()));
          }
        } else {
          result = await updateResponse.json();
          this.backupFileId = result.id;
        }
      }

      if (!result) {
        // Create new file
        result = await uploadToGoogleDrive({
          accessToken: this.accessToken,
          file: blob,
          fileName,
          folderId: this.appFolderId
        });
        this.backupFileId = result.id;

        // Fetch modifiedTime for the newly created file
        try {
          const metaUrl = `https://www.googleapis.com/drive/v3/files/${result.id}?fields=modifiedTime`;
          const metaResponse = await fetch(metaUrl, {
            headers: { Authorization: `Bearer ${this.accessToken}` }
          });
          if (metaResponse.ok) {
            const meta = await metaResponse.json();
            result.modifiedTime = meta.modifiedTime;
          }
        } catch (e) {
          // Non-critical — next sync will re-fetch
        }
      }

      // Update sync metadata with modifiedTime for future comparisons
      this.updateSyncMetadata({
        lastSyncTime: Date.now(),
        lastSyncHash: data._metadata.hash,
        lastUploadFileId: result.id,
        lastCloudModifiedTime: result.modifiedTime || null,
      });

      return result;
    } catch (error) {
      secureLogger.error('Failed to upload data:', error);
      throw error;
    }
  }

  // Download data from Google Drive
  // Uses cached fileId to skip the search API call
  async downloadFromCloud() {
    try {
      if (!this.accessToken || !this.appFolderId) {
        secureLogger.debug('Cloud sync not initialized, skipping download');
        return null;
      }

      if (!navigator.onLine) {
        secureLogger.debug('Device is offline, skipping cloud download');
        return null;
      }

      let fileId = this.backupFileId;
      let modifiedTime = null;

      if (!fileId) {
        // Search for backup file (only when no cached ID)
        const query = `name='${SYNC_CONFIG.BACKUP_FILE_NAME}' and '${this.appFolderId}' in parents and trashed=false`;
        const searchUrl = `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(query)}&fields=files(id,name,modifiedTime)&orderBy=modifiedTime desc`;

        const searchResponse = await fetch(searchUrl, {
          headers: { Authorization: `Bearer ${this.accessToken}` }
        });

        if (!searchResponse.ok) {
          if (searchResponse.status === 401) {
            secureLogger.debug('Access token expired, clearing auth state');
            this.clearAuthenticationState();
            return null;
          }
          throw new Error(`Failed to search for backup file: ${searchResponse.status} ${searchResponse.statusText}`);
        }

        const searchData = await searchResponse.json();

        if (searchData.files.length === 0) {
          secureLogger.debug('No backup file found in cloud');
          return null;
        }

        fileId = searchData.files[0].id;
        modifiedTime = searchData.files[0].modifiedTime;
        this.backupFileId = fileId;
      }

      // Download file content directly using fileId
      const downloadUrl = `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`;
      const downloadResponse = await fetch(downloadUrl, {
        headers: { Authorization: `Bearer ${this.accessToken}` }
      });

      if (!downloadResponse.ok) {
        if (downloadResponse.status === 404) {
          // File deleted — clear cache
          this.backupFileId = null;
          return null;
        }
        throw new Error(`Failed to download backup file: ${downloadResponse.status} ${downloadResponse.statusText}`);
      }

      const backupData = await downloadResponse.json();

      return {
        data: backupData,
        fileId,
        modifiedTime,
      };
    } catch (error) {
      secureLogger.debug('Failed to download from cloud:', {
        message: error.message,
        hasToken: !!this.accessToken,
        hasFolder: !!this.appFolderId,
        isOnline: navigator.onLine
      });
      // Don't throw error for optional cloud sync functionality
      return null;
    }
  }

  // Apply cloud data to local storage.
  //
  // Bumps cloudApplyDepth (re-entrant counter) so data-change hooks ignore
  // writes triggered by this function. Decrements in finally — including
  // on early returns (the previous boolean implementation leaked a stuck-true
  // state on every "local_newer" skip, silently disabling dirty tracking).
  //
  // Verifies the cloud blob's embedded SHA-256 hash before touching local
  // data. On mismatch, refuses to apply (possible corruption / tampering).
  async applyCloudDataToLocal(cloudData, conflictResolution = 'latest_wins') {
    const metadata = this.getSyncMetadata();
    // `force` = manual restore (file import or versioned-snapshot rollback).
    // Semantics: full replace, bypass hash gating (user may have legacy or
    // hand-edited blobs), bypass per-row merge (imported rows must win even
    // if local updatedAt is newer — otherwise restore looks like a no-op).
    const isForce = conflictResolution === 'force';

    // Integrity check: re-hash the blob (with hash field swapped to a
    // placeholder) and compare to the embedded hash. Legacy blobs without
    // a hash field pass through.
    const hashOk = await this.verifyDataHash(cloudData);
    if (!hashOk) {
      if (isForce) {
        secureLogger.warn('Hash mismatch on force apply — continuing (manual restore)');
      } else {
        secureLogger.error('Cloud data hash mismatch — refusing to apply (possible corruption)');
        this.notifyListeners('sync_failed', {
          type: 'apply',
          error: 'hash_mismatch',
          timestamp: Date.now(),
        });
        return { applied: false, reason: 'hash_mismatch' };
      }
    }

    // Schema version gate. Newer-than-current = refuse (old client must not
    // downgrade fresh data). Older = run forward through the migrator before
    // touching local storage. Legacy blobs without the field are treated as v1.
    const incomingVersion = cloudData?._metadata?.schemaVersion ?? 1;
    if (incomingVersion > CURRENT_SCHEMA_VERSION) {
      secureLogger.error(
        `Cloud schema v${incomingVersion} > supported v${CURRENT_SCHEMA_VERSION} — refusing to apply (client likely outdated)`
      );
      this.notifyListeners('sync_failed', {
        type: 'apply',
        error: 'schema_too_new',
        cloudVersion: incomingVersion,
        supportedVersion: CURRENT_SCHEMA_VERSION,
        timestamp: Date.now(),
      });
      return { applied: false, reason: 'schema_too_new' };
    }
    if (incomingVersion < CURRENT_SCHEMA_VERSION) {
      cloudData = this.migrateCloudData(cloudData, incomingVersion);
    }

    // Compact backups ship with an `_assets` map + { $ref: "<sha256>" }
    // placeholders in place of repeated base64 blobs (logo, seal, bg).
    // Union any incoming assets into local IDB BEFORE writing the keys
    // that reference them so refs never dangle. Readers (assetStorage.getItem)
    // expand on the way out to Redux, so downstream consumers are unaware.
    // Legacy (fat) blobs have no `_assets` — this is a no-op for them.
    if (cloudData && cloudData._assets && typeof cloudData._assets === 'object') {
      try {
        await mergeIncomingAssets(cloudData._assets);
      } catch (err) {
        secureLogger.warn('Failed to merge incoming _assets:', err);
      }
    }

    secureLogger.info('Applying cloud data to local storage');
    this.cloudApplyDepth++;
    try {
      // Simple conflict resolution: latest wins
      if (conflictResolution === 'latest_wins') {
        const cloudTimestamp = cloudData._metadata?.timestamp || 0;
        const localTimestamp = metadata.lastSyncTime || 0;

        if (cloudTimestamp <= localTimestamp) {
          secureLogger.info('Local data is newer or equal, skipping sync');
          return { applied: false, reason: 'local_newer' };
        }
      }

      // Apply cloud data to local storage
      const applied = [];
      const failed = [];

      // Archived invoice IDs embedded by the partitioner — preserve these locally
      const archivedInvoiceIds = new Set(cloudData._archivedInvoiceIds || []);

      for (const key of SYNC_KEYS) {
        try {
          if (cloudData[key] !== undefined) {
            let dataToApply = cloudData[key];

            // For invoice keys: preserve locally-held archived invoices that were
            // excluded from the active backup (they live in cold_storage/ on Drive)
            if ((key === INVOICES_KEY || key === INVOICE_DETAILS) && archivedInvoiceIds.size > 0) {
              const localData = await localforage.getItem(key) || [];
              const cloudIds = new Set(dataToApply.map(item => item.id));
              const localArchived = localData.filter(
                item => archivedInvoiceIds.has(item.id) && !cloudIds.has(item.id)
              );
              if (localArchived.length > 0) {
                dataToApply = [...dataToApply, ...localArchived];
                secureLogger.info(`Preserved ${localArchived.length} archived items for ${key}`);
              }
            }

            // Per-row merge for array entities: keep the row whose updatedAt
            // is newest (tombstones included). Without this, a sync from
            // device B would overwrite an unrelated edit made on device A.
            // Other keys (forms, defaults, company) are single-object —
            // replace-behavior is correct.
            //
            // Force mode (manual restore) skips the merge: the user explicitly
            // asked to roll back, so imported rows must win even if locals are
            // newer. Otherwise a restore looks like a no-op.
            if (!isForce && MERGEABLE_ARRAY_KEYS.has(key) && Array.isArray(dataToApply)) {
              const localFull = (await localforage.getItem(key)) || [];
              const before = dataToApply.length;
              dataToApply = mergeRowsById(localFull, dataToApply);
              secureLogger.info(
                `Merged ${key}: cloud=${before}, local=${Array.isArray(localFull) ? localFull.length : 0}, merged=${dataToApply.length}`
              );
            }

            // NOTE: subscription limits are enforced at write/UI time via
            // UsageRestriction components — never truncate during cloud apply.
            // Truncating here would persist the smaller array locally and then
            // re-upload it, permanently destroying the overflow rows.

            // Route image-carrying writes through the compacting wrapper so
            // data from legacy (fat) cloud blobs lands deduped. Compact cloud
            // data passes through the fast idempotent path (no big strings
            // found → nothing to hash).
            if (isAssetKey(key)) {
              await compactingSetItem(key, dataToApply);
            } else {
              await localforage.setItem(key, dataToApply);
            }
            applied.push(key);
            secureLogger.info(`Applied ${key} from cloud`);
          }
        } catch (error) {
          secureLogger.error(`Failed to apply ${key}:`, error);
          failed.push({ key, error: error.message });
        }
      }

      // Update metadata
      this.updateSyncMetadata({
        lastSyncTime: Date.now(),
        lastSyncHash: cloudData._metadata?.hash,
      });

      secureLogger.info(`Sync completed: ${applied.length} applied, ${failed.length} failed`);

      return {
        applied: true,
        appliedKeys: applied,
        failedKeys: failed,
        timestamp: Date.now(),
      };
    } finally {
      this.cloudApplyDepth = Math.max(0, this.cloudApplyDepth - 1);
    }
  }

  // Re-hash the blob (excluding the hash field itself) and compare to the
  // embedded _metadata.hash. Returns true for legacy blobs missing the field
  // so we don't break first sync after upgrade.
  async verifyDataHash(cloudData) {
    const embedded = cloudData?._metadata?.hash;
    if (!embedded) return true; // legacy / pre-rehash blob — nothing to verify
    const meta = { ...cloudData._metadata, hash: HASH_PLACEHOLDER };
    const blob = { ...cloudData, _metadata: meta };
    const computed = await this.generateDataHash(blob);
    return computed === embedded;
  }

  // Perform sync with incremental optimization
  // Fast paths avoid unnecessary downloads/uploads when nothing changed
  async performSync(syncType = 'manual') {
    if (this.syncInProgress) {
      secureLogger.info('Sync already in progress, skipping');
      return { success: false, reason: 'sync_in_progress' };
    }

    if (!this.isOnline) {
      secureLogger.info('Offline, queuing sync operation');
      this.queueSyncOperation(syncType);
      return { success: false, reason: 'offline' };
    }

    if (!this.accessToken) {
      secureLogger.info('No access token available');
      return { success: false, reason: 'no_token' };
    }

    // Proactive: if the stored token is within the expiring-soon window
    // (default 5 min before TTL), broadcast a refresh request now so a
    // listener can silently re-prompt sign-in before this request 401s.
    if (isStoredTokenExpiringSoon()) {
      requestTokenRefresh('pre_sync');
    }

    this.syncInProgress = true;
    this.notifyListeners('sync_started', { type: syncType });

    try {
      const hasDirtyKeys = this.dirtyKeys.size > 0;
      const metadata = this.getSyncMetadata();

      // Step 1: Lightweight metadata-only check (no file content downloaded)
      const cloudMeta = await this.getCloudFileMetadata();
      const storedCloudModifiedTime = metadata.lastCloudModifiedTime;
      const cloudChanged = cloudMeta && cloudMeta.modifiedTime !== storedCloudModifiedTime;

      // ── Fast path: nothing changed anywhere ──
      if (!hasDirtyKeys && !cloudChanged && cloudMeta) {
        secureLogger.info('No changes detected (metadata-only check), skipping sync');
        this.notifyListeners('sync_completed', {
          type: syncType, action: 'no_change', success: true
        });
        return { success: true, action: 'no_change' };
      }

      // ── Fast path: only local changed, cloud untouched ──
      if (hasDirtyKeys && !cloudChanged && cloudMeta) {
        secureLogger.info(`Local changes in [${[...this.dirtyKeys].join(', ')}], uploading`);
        const localData = await this.getAllLocalData();
        await this._uploadPartitioned(localData, syncType);
        this.dirtyKeys.clear();
        this.persistSyncStateSoon();
        this.notifyListeners('sync_completed', {
          type: syncType, action: 'upload_local', success: true
        });
        return { success: true, action: 'upload_local' };
      }

      // ── No cloud file yet — initial upload ──
      if (!cloudMeta) {
        secureLogger.info('No cloud data found, uploading local data');
        const localData = await this.getAllLocalData();
        await this._uploadPartitioned(localData, syncType);
        this.dirtyKeys.clear();
        this.persistSyncStateSoon();
        this.notifyListeners('sync_completed', {
          type: syncType, action: 'upload_initial', success: true
        });
        return { success: true, action: 'upload_initial' };
      }

      // ── Fast path: only cloud changed, no local edits ──
      if (cloudChanged && !hasDirtyKeys) {
        secureLogger.info('Cloud data changed, downloading');
        const cloudResult = await this.downloadFromCloud();
        if (cloudResult) {
          await this.applyCloudDataToLocal(cloudResult.data);
          this.updateSyncMetadata({ lastCloudModifiedTime: cloudResult.modifiedTime });

          // On initialize sync, also restore archived invoices
          if (syncType === 'initialize') {
            this.mergeArchiveIntoLocal(await this.downloadArchiveFromCloud()).catch(e =>
              secureLogger.warn('Archive restore during init skipped:', e)
            );
          }

          this.notifyListeners('sync_completed', {
            type: syncType, action: 'download_applied', success: true
          });
          return { success: true, action: 'download_applied' };
        }
      }

      // ── Both changed — full conflict resolution ──
      secureLogger.info('Both local and cloud changed, resolving conflicts');
      const localData = await this.getAllLocalData();
      const cloudResult = await this.downloadFromCloud();

      if (!cloudResult) {
        // Cloud file disappeared — upload local
        await this._uploadPartitioned(localData, syncType);
        this.dirtyKeys.clear();
        this.persistSyncStateSoon();
        this.notifyListeners('sync_completed', {
          type: syncType, action: 'upload_initial', success: true
        });
        return { success: true, action: 'upload_initial' };
      }

      // Timestamp-based conflict resolution
      const cloudTimestamp = cloudResult.data._metadata?.timestamp || 0;
      const localTimestamp = metadata.lastSyncTime || 0;

      if (cloudTimestamp > localTimestamp) {
        // Cloud is newer — apply cloud data, then re-upload merged result
        secureLogger.info('Cloud data is newer, applying and merging local changes');
        const applyResult = await this.applyCloudDataToLocal(cloudResult.data);
        const mergedData = await this.getAllLocalData();
        await this._uploadPartitioned(mergedData, syncType);
        this.dirtyKeys.clear();
        this.persistSyncStateSoon();
        this.notifyListeners('sync_completed', {
          type: syncType, action: 'download_applied', success: true, result: applyResult
        });
        return { success: true, action: 'download_applied', result: applyResult };
      } else {
        // Local is newer — upload
        secureLogger.info('Local data is newer, uploading to cloud');
        await this._uploadPartitioned(localData, syncType);
        this.dirtyKeys.clear();
        this.persistSyncStateSoon();
        this.notifyListeners('sync_completed', {
          type: syncType, action: 'upload_local', success: true
        });
        return { success: true, action: 'upload_local' };
      }

    } catch (error) {
      secureLogger.debug('Sync failed (cloud sync is optional):', {
        message: error.message,
        syncType,
        hasToken: !!this.accessToken,
        hasFolder: !!this.appFolderId,
        isOnline: navigator.onLine
      });

      this.notifyListeners('sync_failed', {
        type: syncType,
        error: error.message,
        timestamp: Date.now()
      });

      if (syncType === 'auto' || syncType === 'data_change') {
        this.queueSyncOperation('retry');
      }

      return { success: false, error: error.message };
    } finally {
      this.syncInProgress = false;
    }
  }

  // Queue sync operation for when online
  queueSyncOperation(syncType) {
    this.syncQueue.push({
      type: syncType,
      timestamp: Date.now(),
    });
    this.persistSyncStateSoon();
  }

  // Process queued sync operations
  async processQueuedSyncOperations() {
    if (this.syncQueue.length === 0) return;

    secureLogger.info(`Processing ${this.syncQueue.length} queued sync operations`);

    // Get latest queued operation
    const latestOperation = this.syncQueue.pop();
    this.syncQueue = []; // Clear queue
    this.persistSyncStateSoon();

    try {
      await this.performSync(latestOperation.type);
    } catch (error) {
      secureLogger.error('Failed to process sync queue:', error);
    }
  }

  // Force sync (manual trigger)
  async forceSync() {
    secureLogger.info('Force sync triggered');
    return await this.performSync('manual');
  }

  // Event listener management
  addEventListener(callback) {
    this.eventListeners.add(callback);
  }

  removeEventListener(callback) {
    this.eventListeners.delete(callback);
  }

  notifyListeners(event, data = {}) {
    this.eventListeners.forEach(callback => {
      try {
        if (typeof callback === 'function') {
          callback(event, data);
        }
      } catch (error) {
        secureLogger.error('Error in sync event listener:', {
          error: error.message || 'Unknown error',
          event,
          stack: error.stack
        });
      }
    });
  }

  // Export data for manual backup
  async exportBackup() {
    try {
      const data = await this.getAllLocalData();
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: 'application/json'
      });
      
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `logislip_backup_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      secureLogger.info('Backup exported successfully');
      return true;
    } catch (error) {
      secureLogger.error('Failed to export backup:', error);
      throw error;
    }
  }

  // Import data from backup file
  //
  // Gotchas this handles (regression history):
  //   1. Legacy / hand-edited exports may lack `_metadata`. We stamp a minimal
  //      one so the schema + hash checks downstream don't reject a valid file.
  //   2. `force` routes through applyCloudDataToLocal without hash gating and
  //      without per-row merge (see isForce there). Without skipping merge,
  //      an import would be silently discarded because local updatedAt is
  //      almost always newer than the backup's.
  //   3. After a successful import the next sync MUST push (not pull), else
  //      the stale Drive copy would overwrite the restore within minutes.
  //      We null lastCloudModifiedTime + lastSyncHash and mark every sync
  //      key dirty so the next sync takes the "upload_local" branch.
  async importBackup(file) {
    try {
      const text = await file.text();
      const data = JSON.parse(text);

      if (!data || typeof data !== 'object') {
        throw new Error('Invalid backup file: not a JSON object');
      }

      // Accept legacy exports that lack _metadata — stamp a minimal one so
      // the schema/hash checks treat it as a v1 restore candidate.
      if (!data._metadata) {
        secureLogger.warn('Import backup missing _metadata — treating as legacy export');
        data._metadata = {
          timestamp: Date.now(),
          schemaVersion: 1,
        };
      }

      // Quick sanity: at least one known data key must be present
      const hasAnyKey = SYNC_KEYS.some(k => data[k] !== undefined);
      if (!hasAnyKey) {
        throw new Error('Invalid backup file: no recognized data keys');
      }

      // Force apply: skips hash gate, skips per-row merge (full replace)
      const result = await this.applyCloudDataToLocal(data, 'force');

      if (!result || !result.applied) {
        throw new Error(`Import refused: ${result?.reason || 'unknown'}`);
      }

      // Decouple from cloud so the next sync uploads the restored payload.
      this.updateSyncMetadata({
        lastCloudModifiedTime: null,
        lastSyncHash: null,
      });
      for (const key of SYNC_KEYS) {
        this.dirtyKeys.add(key);
      }
      this.persistSyncStateSoon();

      secureLogger.info('Backup imported successfully');

      this.notifyListeners('backup_imported', {
        success: true,
        result,
      });

      return { success: true, result };
    } catch (error) {
      secureLogger.error('Failed to import backup:', error);

      this.notifyListeners('backup_imported', {
        success: false,
        error: error.message,
      });

      return { success: false, error: error.message };
    }
  }

  // Check if service is initialized
  isInitialized() {
    return !!(this.accessToken && this.userId);
  }

  // Get sync status
  getSyncStatus() {
    const metadata = this.getSyncMetadata();
    return {
      isInitialized: this.isInitialized(),
      isOnline: this.isOnline,
      hasToken: !!this.accessToken,
      lastSyncTime: metadata?.lastSyncTime,
      syncInProgress: this.syncInProgress,
      queuedOperations: this.syncQueue.length,
      autoSyncEnabled: metadata?.autoSyncEnabled,
      deviceId: metadata?.deviceId,
      pendingChanges: this.dirtyKeys.size,
      archivedInvoiceCount: metadata?.lastArchiveCount || 0,
      archiveThresholdDays: SYNC_CONFIG.ARCHIVE_THRESHOLD_DAYS,
    };
  }

  // Toggle auto sync
  toggleAutoSync(enabled) {
    this.updateSyncMetadata({ autoSyncEnabled: enabled });
    
    if (enabled) {
      this.setupAutoSync();
      secureLogger.info('Auto sync enabled');
    } else {
      if (this.autoSyncInterval) {
        clearInterval(this.autoSyncInterval);
        this.autoSyncInterval = null;
      }
      secureLogger.info('Auto sync disabled');
    }
  }

  // Logout and clear authentication state
  logout() {
    // Clear authentication state
    this.clearAuthenticationState();

    // Stop auto sync
    if (this.autoSyncInterval) {
      clearInterval(this.autoSyncInterval);
      this.autoSyncInterval = null;
    }

    // Preserve dirtyKeys across logout so offline edits made before sign-out
    // are not abandoned: the next sign-in still uploads pending deltas.
    this.syncQueue = [];
    this.persistSyncStateSoon();

    // Reset sync status
    this.syncInProgress = false;
    
    // Notify listeners
    this.notifyListeners('logged_out', { success: true });
    
    secureLogger.info('Logged out from cloud sync');
  }

  // Cleanup
  destroy() {
    if (this.autoSyncInterval) {
      clearInterval(this.autoSyncInterval);
    }
    
    if (this.dataSyncTimeout) {
      clearTimeout(this.dataSyncTimeout);
    }
    
    window.removeEventListener('online', this.processQueuedSyncOperations);
    window.removeEventListener('offline', () => {});
    
    this.eventListeners.clear();
    secureLogger.info('Cloud Sync Service destroyed');
  }

  // ═══════════════════════════════════════════════════════════════
  //  COLD-STORAGE / ARCHIVE SYSTEM
  //  Paid invoices older than ARCHIVE_THRESHOLD_DAYS are moved to
  //  a separate cold_storage/ folder so the daily active backup
  //  stays small and fast.
  // ═══════════════════════════════════════════════════════════════

  // Split invoices into active (synced daily) and archive (synced rarely)
  partitionInvoiceData(allData) {
    const thresholdMs = SYNC_CONFIG.ARCHIVE_THRESHOLD_DAYS * 24 * 60 * 60 * 1000;
    const cutoffDate = Date.now() - thresholdMs;

    const invoices = allData[INVOICES_KEY] || [];
    const invoiceDetails = allData[INVOICE_DETAILS] || [];

    const activeInvoices = [];
    const archivedInvoices = [];
    const archivedIds = new Set();

    for (const inv of invoices) {
      const createdTime = new Date(inv.createdDate).getTime();
      if (inv.statusIndex === '3' && createdTime < cutoffDate) {
        archivedInvoices.push(inv);
        archivedIds.add(inv.id);
      } else {
        activeInvoices.push(inv);
      }
    }

    const activeDetails = [];
    const archivedDetails = [];

    for (const detail of invoiceDetails) {
      if (archivedIds.has(detail.id)) {
        archivedDetails.push(detail);
      } else {
        activeDetails.push(detail);
      }
    }

    // Build active data — everything except archived invoices
    const activeData = { ...allData };
    activeData[INVOICES_KEY] = activeInvoices;
    activeData[INVOICE_DETAILS] = activeDetails;
    // Embed archived IDs so other devices know not to delete them on download
    activeData._archivedInvoiceIds = Array.from(archivedIds);

    return {
      activeData,
      archiveData: {
        invoices: archivedInvoices,
        invoiceDetails: archivedDetails,
        _metadata: {
          archivedAt: Date.now(),
          thresholdDays: SYNC_CONFIG.ARCHIVE_THRESHOLD_DAYS,
          count: archivedInvoices.length,
        },
      },
      archivedIds,
    };
  }

  // Find or create cold_storage/ folder inside the user's app folder
  async setupArchiveFolder() {
    if (this.archiveFolderId) return this.archiveFolderId;

    const folder = await findOrCreateGoogleDriveFolder({
      accessToken: this.accessToken,
      folderName: SYNC_CONFIG.ARCHIVE_FOLDER_NAME,
      parentId: this.appFolderId,
    });
    this.archiveFolderId = folder.id;
    return folder.id;
  }

  // Merge newly-archived invoices with the existing archive file (dedup by ID)
  mergeArchiveData(existingArchive, newArchiveData) {
    if (!existingArchive) return newArchiveData;

    const existingInvIds = new Set((existingArchive.invoices || []).map(i => i.id));
    const existingDetIds = new Set((existingArchive.invoiceDetails || []).map(d => d.id));

    const mergedInvoices = [...(existingArchive.invoices || [])];
    const mergedDetails = [...(existingArchive.invoiceDetails || [])];

    for (const inv of newArchiveData.invoices) {
      if (!existingInvIds.has(inv.id)) {
        mergedInvoices.push(inv);
      }
    }
    for (const detail of newArchiveData.invoiceDetails) {
      if (!existingDetIds.has(detail.id)) {
        mergedDetails.push(detail);
      }
    }

    return {
      invoices: mergedInvoices,
      invoiceDetails: mergedDetails,
      _metadata: {
        ...newArchiveData._metadata,
        count: mergedInvoices.length,
        lastMergedAt: Date.now(),
      },
    };
  }

  // Upload (or merge-then-upload) archived invoices to cold_storage/
  async uploadArchiveToCloud(archiveData) {
    try {
      const folderId = await this.setupArchiveFolder();

      // Download existing archive to merge (don't overwrite — append only)
      const existing = await this.downloadArchiveFromCloud();
      const merged = this.mergeArchiveData(existing, archiveData);

      const blob = new Blob([JSON.stringify(merged, null, 2)], {
        type: 'application/json',
      });

      // Try cached fileId first
      let fileId = this.archiveFileId;
      if (fileId) {
        const updateUrl = `https://www.googleapis.com/upload/drive/v3/files/${fileId}?uploadType=media&fields=id,modifiedTime`;
        const response = await fetch(updateUrl, {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${this.accessToken}`,
            'Content-Type': 'application/json',
          },
          body: blob,
        });
        if (response.ok) {
          const result = await response.json();
          this.archiveFileId = result.id;
          secureLogger.info(`Archive updated: ${merged.invoices.length} invoices in cold storage`);
          return result;
        }
        if (response.status === 404) {
          this.archiveFileId = null;
          fileId = null;
        }
      }

      if (!fileId) {
        // Search for existing file
        const query = `name='${SYNC_CONFIG.ARCHIVE_FILE_NAME}' and '${folderId}' in parents and trashed=false`;
        const searchUrl = `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(query)}&fields=files(id)`;
        const searchRes = await fetch(searchUrl, {
          headers: { Authorization: `Bearer ${this.accessToken}` },
        });
        if (searchRes.ok) {
          const searchData = await searchRes.json();
          if (searchData.files.length > 0) {
            fileId = searchData.files[0].id;
            this.archiveFileId = fileId;
            // Update existing
            const updateUrl = `https://www.googleapis.com/upload/drive/v3/files/${fileId}?uploadType=media&fields=id,modifiedTime`;
            const updateRes = await fetch(updateUrl, {
              method: 'PATCH',
              headers: {
                Authorization: `Bearer ${this.accessToken}`,
                'Content-Type': 'application/json',
              },
              body: blob,
            });
            if (updateRes.ok) {
              const result = await updateRes.json();
              secureLogger.info(`Archive updated: ${merged.invoices.length} invoices in cold storage`);
              return result;
            }
          }
        }

        // Create new archive file
        const result = await uploadToGoogleDrive({
          accessToken: this.accessToken,
          file: blob,
          fileName: SYNC_CONFIG.ARCHIVE_FILE_NAME,
          folderId,
        });
        this.archiveFileId = result.id;
        secureLogger.info(`Archive created: ${merged.invoices.length} invoices in cold storage`);
        return result;
      }
    } catch (error) {
      secureLogger.warn('Failed to upload archive to cold storage:', error);
      throw error;
    }
  }

  // Download archived invoices from cold_storage/ (returns null if none)
  async downloadArchiveFromCloud() {
    try {
      if (!this.accessToken || !this.appFolderId) return null;
      if (!navigator.onLine) return null;

      const folderId = await this.setupArchiveFolder();

      let fileId = this.archiveFileId;
      if (!fileId) {
        const query = `name='${SYNC_CONFIG.ARCHIVE_FILE_NAME}' and '${folderId}' in parents and trashed=false`;
        const searchUrl = `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(query)}&fields=files(id)`;
        const response = await fetch(searchUrl, {
          headers: { Authorization: `Bearer ${this.accessToken}` },
        });
        if (!response.ok) return null;
        const data = await response.json();
        if (data.files.length === 0) return null;
        fileId = data.files[0].id;
        this.archiveFileId = fileId;
      }

      const downloadUrl = `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`;
      const downloadRes = await fetch(downloadUrl, {
        headers: { Authorization: `Bearer ${this.accessToken}` },
      });
      if (!downloadRes.ok) {
        if (downloadRes.status === 404) {
          this.archiveFileId = null;
          return null;
        }
        return null;
      }

      return await downloadRes.json();
    } catch (error) {
      secureLogger.debug('Failed to download archive:', { message: error.message });
      return null;
    }
  }

  // Only upload archive when its content has actually changed
  async syncArchiveIfNeeded(archiveData) {
    if (archiveData.invoices.length === 0) return;

    const metadata = this.getSyncMetadata();
    const currentArchiveHash = await this.generateDataHash(archiveData);

    if (currentArchiveHash === metadata.lastArchiveHash) {
      return; // No new invoices to archive
    }

    await this.uploadArchiveToCloud(archiveData);
    this.updateSyncMetadata({
      lastArchiveHash: currentArchiveHash,
      lastArchiveCount: archiveData.invoices.length,
    });
  }

  // Search archived invoices by invoice number, client name, or ID
  // Downloads the archive file on demand (cached after first call)
  async searchArchive(query) {
    try {
      const archive = await this.downloadArchiveFromCloud();
      if (!archive || !archive.invoices) {
        return { invoices: [], invoiceDetails: [], totalArchived: 0 };
      }

      const q = query.toLowerCase();
      const matchingInvoices = archive.invoices.filter(inv =>
        (inv.invoiceNo && inv.invoiceNo.toLowerCase().includes(q)) ||
        (inv.clientName && inv.clientName.toLowerCase().includes(q)) ||
        (inv.id && inv.id.toLowerCase().includes(q))
      );

      const matchingIds = new Set(matchingInvoices.map(i => i.id));
      const matchingDetails = archive.invoiceDetails.filter(d => matchingIds.has(d.id));

      return {
        invoices: matchingInvoices,
        invoiceDetails: matchingDetails,
        totalArchived: archive.invoices.length,
      };
    } catch (error) {
      secureLogger.error('Archive search failed:', error);
      return { invoices: [], invoiceDetails: [], totalArchived: 0, error: error.message };
    }
  }

  // Merge archive invoices into local storage (used during full restore)
  async mergeArchiveIntoLocal(archiveData) {
    if (!archiveData || !archiveData.invoices || archiveData.invoices.length === 0) return;

    const localInvoices = await localforage.getItem(INVOICES_KEY) || [];
    const localDetails = await localforage.getItem(INVOICE_DETAILS) || [];

    const localIds = new Set(localInvoices.map(i => i.id));

    const newInvoices = archiveData.invoices.filter(i => !localIds.has(i.id));
    const newDetails = archiveData.invoiceDetails.filter(d => !localIds.has(d.id));

    if (newInvoices.length > 0) {
      this.cloudApplyDepth++;
      try {
        // Archive payloads may still be in legacy fat shape. Route writes
        // through the compacting wrapper so restored rows land deduped.
        await compactingSetItem(INVOICES_KEY, [...localInvoices, ...newInvoices]);
        await compactingSetItem(INVOICE_DETAILS, [...localDetails, ...newDetails]);
      } finally {
        this.cloudApplyDepth = Math.max(0, this.cloudApplyDepth - 1);
      }
      secureLogger.info(`Restored ${newInvoices.length} archived invoices to local storage`);
    }
  }

  // Full restore: download active backup + cold-storage archive
  // Use on a new device or to recover all historical data
  async performFullRestore() {
    try {
      secureLogger.info('Starting full restore (active + archive)');

      // 1. Download and apply active backup
      const activeResult = await this.downloadFromCloud();
      if (activeResult) {
        await this.applyCloudDataToLocal(activeResult.data, 'force');
      }

      // 2. Download archive and merge into local
      const archiveData = await this.downloadArchiveFromCloud();
      if (archiveData) {
        await this.mergeArchiveIntoLocal(archiveData);
      }

      this.notifyListeners('full_restore_completed', {
        success: true,
        activeRestored: !!activeResult,
        archiveRestored: !!archiveData,
        archivedCount: archiveData?.invoices?.length || 0,
      });

      return {
        success: true,
        activeRestored: !!activeResult,
        archiveRestored: !!archiveData,
        archivedCount: archiveData?.invoices?.length || 0,
      };
    } catch (error) {
      secureLogger.error('Full restore failed:', error);
      this.notifyListeners('full_restore_completed', {
        success: false,
        error: error.message,
      });
      return { success: false, error: error.message };
    }
  }

  // Get archive status without downloading full file
  async getArchiveStatus() {
    const metadata = this.getSyncMetadata();
    return {
      lastArchiveCount: metadata.lastArchiveCount || 0,
      lastArchiveHash: metadata.lastArchiveHash || null,
      thresholdDays: SYNC_CONFIG.ARCHIVE_THRESHOLD_DAYS,
      hasColdStorage: !!(this.archiveFileId || metadata.lastArchiveCount > 0),
    };
  }

  // Helper: upload with automatic active/archive partitioning
  //
  // MIGRATION SAFETY (existing users):
  //   - First time: archive is uploaded, but the active backup still contains
  //     ALL invoices (archiveMigrated flag is false). This means if the user
  //     rolls back to old code, their backup is still complete.
  //   - Second sync onward: archiveMigrated is true, so the active backup
  //     only contains non-archived invoices (the fast path).
  //
  // UPLOAD SAFETY:
  //   - Archived invoices are only stripped from the active backup AFTER
  //     the archive file is confirmed on Drive.
  //   - If archive upload fails, the full un-partitioned data is uploaded
  //     instead, so no data is ever lost.
  async _uploadPartitioned(data, syncType) {
    const { activeData, archiveData } = this.partitionInvoiceData(data);

    if (archiveData.invoices.length === 0) {
      // Nothing to archive — upload everything as-is
      await this.uploadToCloud(activeData, syncType);
      return;
    }

    const metadata = this.getSyncMetadata();
    const currentArchiveHash = await this.generateDataHash(archiveData);
    const archiveUnchanged = currentArchiveHash === metadata.lastArchiveHash;

    // ── Migration: first-ever partition for this user ──
    // Upload the archive but keep the active backup FULL (all invoices)
    // so a rollback to old code won't lose data.
    if (!metadata.archiveMigrated) {
      try {
        await this.uploadArchiveToCloud(archiveData);
        this.updateSyncMetadata({
          lastArchiveHash: currentArchiveHash,
          lastArchiveCount: archiveData.invoices.length,
          archiveMigrated: true,  // Next sync will use the slim active backup
        });
        secureLogger.info('Archive migration: cold-storage created, full backup preserved for this cycle');
      } catch (archiveError) {
        secureLogger.warn('Archive migration failed, continuing with full backup:', archiveError);
      }
      // Upload FULL data this time (safe for old-code rollback)
      await this.uploadToCloud(data, syncType);
      return;
    }

    // ── Steady state: archive already established ──
    if (archiveUnchanged) {
      // Archive on Drive is already up-to-date — upload active-only
      await this.uploadToCloud(activeData, syncType);
      return;
    }

    // Archive has new content — upload archive FIRST, then active
    try {
      await this.uploadArchiveToCloud(archiveData);
      this.updateSyncMetadata({
        lastArchiveHash: currentArchiveHash,
        lastArchiveCount: archiveData.invoices.length,
      });
      // Archive confirmed on Drive — safe to upload the smaller active backup
      await this.uploadToCloud(activeData, syncType);
    } catch (archiveError) {
      // Archive upload failed — fall back to uploading FULL data (no data loss)
      secureLogger.warn('Archive upload failed, uploading full backup as fallback:', archiveError);
      await this.uploadToCloud(data, syncType);
    }
  }

  // Public API for syncedStorage / slices: mark a SYNC_KEY dirty and schedule
  // a debounced auto-sync. Replaces the localforage monkey-patch so callers
  // own their writes explicitly. No-op when:
  //   - key isn't in SYNC_KEYS (forms/settings outside the sync set)
  //   - sync isn't initialized yet (boot before Drive auth)
  //   - we're inside a cloudApplyDepth scope (cloud->local apply must not
  //     bounce back as a "user change" and re-upload what we just downloaded)
  triggerSync(key, op = 'update') {
    if (!SYNC_KEYS.includes(key)) return;
    if (!this.isInitialized()) return;
    if (this.cloudApplyDepth !== 0) return;

    this.dirtyKeys.add(key);
    this.persistSyncStateSoon();
    secureLogger.info(`Data ${op} in ${key}, marked dirty`);

    if (this.dataSyncTimeout) {
      clearTimeout(this.dataSyncTimeout);
    }
    this.dataSyncTimeout = setTimeout(async () => {
      if (this.isOnline && this.getSyncMetadata().autoSyncEnabled) {
        try {
          await this.performSync('data_change');
        } catch (error) {
          secureLogger.warn('Auto-sync failed:', error);
        }
      }
    }, 3000);
  }

  // Initialize sync metadata in localStorage
  initializeSyncMetadata() {
    const existingMetadata = localStorage.getItem('cloudSyncMetadata');
    if (!existingMetadata) {
      const metadata = {
        deviceId: this.generateDeviceId(),
        autoSyncEnabled: true,
        lastSyncTime: null,
        lastCloudModifiedTime: null,
        lastUploadFileId: null,
        lastArchiveHash: null,
        lastArchiveCount: 0,
        previousBackupFileId: null,
        lastBackupRotationTime: null,
        syncVersion: 1,
        initialized: true
      };
      localStorage.setItem('cloudSyncMetadata', JSON.stringify(metadata));
    }
  }

  // Get sync metadata from localStorage
  getSyncMetadata() {
    try {
      const metadata = localStorage.getItem('cloudSyncMetadata');
      if (metadata) {
        return JSON.parse(metadata);
      }
    } catch (error) {
      secureLogger.error('Failed to get sync metadata:', error);
    }
    
    // Return default metadata if none exists
    return {
      deviceId: this.generateDeviceId(),
      autoSyncEnabled: true,
      lastSyncTime: null,
      lastCloudModifiedTime: null,
      lastUploadFileId: null,
      lastArchiveHash: null,
      lastArchiveCount: 0,
      previousBackupFileId: null,
      lastBackupRotationTime: null,
      syncVersion: 1,
      initialized: true
    };
  }

  // Update sync metadata in localStorage
  updateSyncMetadata(updates) {
    try {
      const currentMetadata = this.getSyncMetadata();
      const newMetadata = { ...currentMetadata, ...updates };
      localStorage.setItem('cloudSyncMetadata', JSON.stringify(newMetadata));
    } catch (error) {
      secureLogger.error('Failed to update sync metadata:', error);
    }
  }

  // Generate unique device ID
  generateDeviceId() {
    return `device_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // Setup network listeners for online/offline detection
  setupNetworkListeners() {
    window.addEventListener('online', () => {
      this.isOnline = true;
      secureLogger.info('Network connection restored');
      this.processQueuedSyncOperations();
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
      secureLogger.info('Network connection lost');
    });
  }

  // Setup automatic sync interval based on subscription plan
  setupAutoSync() {
    if (this.autoSyncInterval) {
      clearInterval(this.autoSyncInterval);
    }

    // Get auto-sync frequency from subscription plan
    const getAutoSyncFrequency = () => {
      try {
        // Dynamic import to avoid circular dependency
        return import('../services/subscriptionService').then(module => {
          const subscriptionService = module.default;
          return subscriptionService.getAutoSyncFrequency();
        });
      } catch (error) {
        secureLogger.warn('Could not get auto-sync frequency, defaulting to manual:', error);
        return Promise.resolve(0); // Manual only
      }
    };

    // Setup auto-sync based on plan limitations
    getAutoSyncFrequency().then(frequencyMinutes => {
      if (frequencyMinutes > 0) {
        secureLogger.info(`Setting up auto-sync every ${frequencyMinutes} minutes`);
        this.autoSyncInterval = setInterval(async () => {
          if (this.isOnline && this.getSyncMetadata().autoSyncEnabled && this.isInitialized()) {
            try {
              await this.performSync('auto');
            } catch (error) {
              secureLogger.warn('Auto-sync failed:', error);
            }
          }
        }, frequencyMinutes * 60 * 1000);
      } else {
        secureLogger.info('Auto-sync disabled by subscription plan (manual only)');
      }
    }).catch(error => {
      secureLogger.warn('Failed to setup auto-sync:', error);
    });
  }

  // Clear authentication state
  //
  // We do NOT clear dirtyKeys here. The same user signing back in must be
  // able to resume sync with the deltas they made offline. User-switch is
  // the only path that must wipe dirty state, and it runs its own wipe in
  // _handleUserSwitch.
  clearAuthenticationState() {
    this.accessToken = null;
    this.userId = null;
    this.appFolderId = null;
    this.backupFileId = null;
    this.previousBackupFileId = null;
    this.archiveFolderId = null;
    this.archiveFileId = null;
    this.versionsFolderId = null;

    try {
      clearGoogleAuth();
    } catch (error) {
      secureLogger.error('Failed to clear authentication state:', error);
    }
  }

  // Detect Google-account switch on the same device. If a different userId
  // signs in than the one previously stored, wipe local SYNC_KEYS + sync
  // state so we don't:
  //   1. Upload user A's IndexedDB data into user B's Drive folder
  //   2. Show user A's clients/invoices to user B before initial sync runs
  //
  // Plain logout (no new sign-in) does NOT clear IDB — preserves offline
  // data for accidental sign-outs and lets users keep working pre-auth.
  // Wraps the clears in cloudApplyDepth so the install-time hook ignores
  // them (otherwise the clears would mark every key dirty and trigger a
  // sync uploading empty arrays).
  async _handleUserSwitch(userId) {
    if (!userId) return;
    let lastUserId = null;
    try {
      lastUserId = localStorage.getItem(LAST_USER_KEY);
    } catch (error) {
      // localStorage can throw in private mode / blocked origins — treat
      // as "no prior user" rather than blocking sign-in.
      secureLogger.warn('Failed to read last user id:', error);
    }

    if (lastUserId && lastUserId !== userId) {
      secureLogger.info('User switch detected — clearing local sync caches before initial sync');
      this.cloudApplyDepth++;
      try {
        for (const key of SYNC_KEYS) {
          try {
            await localforage.removeItem(key);
          } catch (error) {
            secureLogger.warn(`Failed to clear ${key} on user switch:`, error);
          }
        }
        // Drop the shared _assets map too — it's tenant-specific (user A's
        // logo bytes are not user B's) and the in-memory cache must reload
        // so the next read doesn't hand out stale images.
        try {
          await localforage.removeItem(ASSETS_KEY);
          invalidateAssetsCache();
        } catch (error) {
          secureLogger.warn('Failed to clear _assets on user switch:', error);
        }
        try {
          await localforage.removeItem(SYNC_STATE_KEY);
        } catch (error) {
          secureLogger.warn('Failed to clear sync state on user switch:', error);
        }
        // Reset in-memory tracking so post-switch sync starts clean
        this.dirtyKeys.clear();
        this.syncQueue = [];
        this.lastSyncTime = null;
        this.backupFileId = null;
        this.previousBackupFileId = null;
        this.archiveFolderId = null;
        this.archiveFileId = null;
        this.versionsFolderId = null;
        // Drop sync metadata that pinned us to the previous user's Drive files
        this.updateSyncMetadata({
          lastSyncTime: null,
          lastCloudModifiedTime: null,
          lastUploadFileId: null,
          lastSyncHash: null,
          lastArchiveHash: null,
          lastArchiveCount: 0,
          previousBackupFileId: null,
          lastBackupRotationTime: null,
        });
      } finally {
        this.cloudApplyDepth = Math.max(0, this.cloudApplyDepth - 1);
      }
      this.notifyListeners('user_switched', { previousUserId: lastUserId, newUserId: userId });
    }

    try {
      localStorage.setItem(LAST_USER_KEY, userId);
    } catch (error) {
      secureLogger.warn('Failed to record last user id:', error);
    }
  }

  // ═══════════════════════════════════════════════════════════════
  //  SYNC-STATE PERSISTENCE
  //  dirtyKeys + syncQueue live in memory and would vanish on tab
  //  close mid-debounce. Persist them to localforage under a dedicated
  //  key so a reload picks up where we left off.
  // ═══════════════════════════════════════════════════════════════

  // Read the persisted state once on startup. Restored entries become
  // pending writes for the next sync.
  async restoreSyncState() {
    try {
      const state = await localforage.getItem(SYNC_STATE_KEY);
      if (!state || typeof state !== 'object') return;
      if (Array.isArray(state.dirtyKeys)) {
        for (const key of state.dirtyKeys) {
          if (SYNC_KEYS.includes(key)) this.dirtyKeys.add(key);
        }
      }
      if (Array.isArray(state.syncQueue)) {
        this.syncQueue.push(...state.syncQueue);
      }
      if (this.dirtyKeys.size > 0 || this.syncQueue.length > 0) {
        secureLogger.info(
          `Restored sync state: ${this.dirtyKeys.size} dirty key(s), ${this.syncQueue.length} queued op(s)`
        );
      }
    } catch (error) {
      secureLogger.warn('Failed to restore sync state:', error);
    }
  }

  // Debounced write — coalesces bursts of dirty-key mutations into one
  // localforage write. Writes via the original (un-hooked) localforage
  // path so it doesn't recursively mark itself dirty.
  persistSyncStateSoon() {
    if (this._persistStateTimer) clearTimeout(this._persistStateTimer);
    this._persistStateTimer = setTimeout(() => {
      this._persistStateTimer = null;
      const payload = {
        dirtyKeys: Array.from(this.dirtyKeys),
        syncQueue: this.syncQueue.slice(0, 32), // cap to avoid runaway
        savedAt: Date.now(),
      };
      // SYNC_STATE_KEY is not in SYNC_KEYS, so the hook will skip it,
      // but we still go through the patched setItem for consistency.
      localforage.setItem(SYNC_STATE_KEY, payload).catch(error => {
        secureLogger.warn('Failed to persist sync state:', error);
      });
    }, 500);
  }

  // ═══════════════════════════════════════════════════════════════
  //  BACKUP ROTATION
  //  Once per BACKUP_ROTATION_INTERVAL_MS, snapshot the current backup
  //  to a "previous" file before overwriting. Gives one-click recovery
  //  if a sync uploads bad data (e.g. an app bug producing empty arrays).
  // ═══════════════════════════════════════════════════════════════

  async rotateBackupIfDue() {
    if (!this.accessToken || !this.appFolderId) return;
    const meta = this.getSyncMetadata();
    const lastRot = meta.lastBackupRotationTime || 0;
    const dueAt = lastRot + SYNC_CONFIG.BACKUP_ROTATION_INTERVAL_MS;
    if (Date.now() < dueAt) return;

    // Need a current backup to rotate. If none exists, this is the first
    // sync — nothing to snapshot yet, just stamp the timestamp.
    const currentId = this.backupFileId || meta.lastUploadFileId;
    if (!currentId) {
      this.updateSyncMetadata({ lastBackupRotationTime: Date.now() });
      return;
    }

    try {
      const snap = await this.createVersionedSnapshot({ manual: false });
      if (snap) {
        // Best-effort: prune old automated snapshots beyond the retention window.
        this.pruneOldVersions().catch(e =>
          secureLogger.warn('Version prune failed:', e?.message || e)
        );
        this.updateSyncMetadata({
          lastBackupRotationTime: Date.now(),
          // Point the legacy "previous backup" pointer at the newest auto
          // snapshot so existing restorePreviousBackup callers still work.
          previousBackupFileId: snap.id,
        });
        this.previousBackupFileId = snap.id;
        secureLogger.info(`Versioned snapshot created: ${snap.name}`);
      }
    } catch (error) {
      secureLogger.warn('Versioned snapshot failed:', error);
    }
  }

  // ═══════════════════════════════════════════════════════════════
  //  VERSIONED SNAPSHOTS  (AWS RDS–style retention)
  //  Each day a server-side copy of the active backup lands in the
  //  `versions/` subfolder with a timestamped name. Automated snaps
  //  are pruned beyond VERSION_AUTO_RETENTION. Manual snaps are kept
  //  forever (distinct filename prefix, distinct description tag).
  // ═══════════════════════════════════════════════════════════════

  async setupVersionsFolder() {
    if (this.versionsFolderId) return this.versionsFolderId;
    const folder = await findOrCreateGoogleDriveFolder({
      accessToken: this.accessToken,
      folderName: SYNC_CONFIG.VERSIONS_FOLDER_NAME,
      parentId: this.appFolderId,
    });
    this.versionsFolderId = folder.id;
    return folder.id;
  }

  // Timestamp safe for Drive filenames (no colons / dots).
  _snapshotTimestamp() {
    return new Date().toISOString().replace(/[:.]/g, '-');
  }

  // Server-side copy of the active backup into versions/. Manual snapshots
  // use a different prefix + description tag so prune skips them.
  async createVersionedSnapshot({ manual = false } = {}) {
    if (!this.accessToken || !this.appFolderId) return null;
    const currentId = this.backupFileId || this.getSyncMetadata().lastUploadFileId;
    if (!currentId) {
      secureLogger.debug('No active backup to snapshot yet');
      return null;
    }

    const folderId = await this.setupVersionsFolder();
    const prefix = manual ? SYNC_CONFIG.VERSION_MANUAL_PREFIX : SYNC_CONFIG.VERSION_AUTO_PREFIX;
    const name = `${prefix}${this._snapshotTimestamp()}.json`;

    const copyUrl = `https://www.googleapis.com/drive/v3/files/${currentId}/copy?fields=id,name,createdTime,size`;
    const res = await fetch(copyUrl, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${this.accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        name,
        parents: [folderId],
        description: manual ? 'manual' : 'auto',
      }),
    });

    if (!res.ok) {
      secureLogger.warn(`Versioned snapshot copy failed: ${res.status}`);
      return null;
    }
    return await res.json();
  }

  // List snapshots in versions/. Sorted newest-first. Manual detection uses
  // the filename prefix (robust across Drive description edits).
  async listBackupVersions() {
    if (!this.accessToken || !this.appFolderId) return [];
    const folderId = await this.setupVersionsFolder();
    const q = `'${folderId}' in parents and trashed=false`;
    const url =
      `https://www.googleapis.com/drive/v3/files?` +
      `q=${encodeURIComponent(q)}` +
      `&fields=files(id,name,createdTime,modifiedTime,size,description)` +
      `&orderBy=createdTime desc` +
      `&pageSize=100`;
    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${this.accessToken}` },
    });
    if (!res.ok) return [];
    const json = await res.json();
    return (json.files || []).map(f => ({
      id: f.id,
      name: f.name,
      createdTime: f.createdTime,
      modifiedTime: f.modifiedTime,
      size: Number(f.size) || 0,
      manual:
        (f.name && f.name.startsWith(SYNC_CONFIG.VERSION_MANUAL_PREFIX)) ||
        f.description === 'manual',
    }));
  }

  // Download a specific version and apply it via force (bypasses hash + merge).
  // Mirrors the importBackup "decouple from cloud" step so the next sync
  // pushes the restored payload instead of pulling the old Drive copy back.
  async restoreBackupVersion(versionId) {
    try {
      if (!this.accessToken || !versionId) {
        throw new Error('Not authenticated or missing versionId');
      }
      const dlUrl = `https://www.googleapis.com/drive/v3/files/${versionId}?alt=media`;
      const dlRes = await fetch(dlUrl, {
        headers: { Authorization: `Bearer ${this.accessToken}` },
      });
      if (!dlRes.ok) {
        throw new Error(`Failed to download version: ${dlRes.status}`);
      }
      const data = await dlRes.json();
      const result = await this.applyCloudDataToLocal(data, 'force');
      if (!result || !result.applied) {
        throw new Error(`Restore refused: ${result?.reason || 'unknown'}`);
      }

      this.updateSyncMetadata({
        lastCloudModifiedTime: null,
        lastSyncHash: null,
      });
      for (const key of SYNC_KEYS) this.dirtyKeys.add(key);
      this.persistSyncStateSoon();

      this.notifyListeners('version_restored', { success: true, versionId, result });
      return { success: true, result };
    } catch (error) {
      secureLogger.error('Failed to restore version:', error);
      this.notifyListeners('version_restored', { success: false, error: error.message });
      return { success: false, error: error.message };
    }
  }

  // Prune automated snapshots beyond VERSION_AUTO_RETENTION. Manual snapshots
  // are never touched. Deletes are best-effort (logged, never thrown).
  async pruneOldVersions(retainCount = SYNC_CONFIG.VERSION_AUTO_RETENTION) {
    const all = await this.listBackupVersions();
    const auto = all.filter(v => !v.manual);
    if (auto.length <= retainCount) return { deleted: 0 };

    const toDelete = auto.slice(retainCount); // list is sorted desc; tail is oldest
    let deleted = 0;
    for (const v of toDelete) {
      try {
        const res = await fetch(`https://www.googleapis.com/drive/v3/files/${v.id}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${this.accessToken}` },
        });
        if (res.ok || res.status === 204) deleted++;
      } catch (e) {
        secureLogger.warn('Prune delete failed:', e?.message || e);
      }
    }
    secureLogger.info(`Pruned ${deleted} old automated snapshots`);
    return { deleted };
  }

  // Download the previous-generation backup. Returns parsed JSON or null.
  // Wire this to a UI button (e.g. Settings → "Restore previous backup")
  // for one-click rollback after a bad sync.
  //
  // Prefers the newest automated snapshot in versions/ (AWS RDS–style).
  // Falls back to the legacy single-slot file so upgraded users with no
  // versioned snapshots yet can still roll back their last sync.
  async restorePreviousBackup() {
    try {
      if (!this.accessToken || !this.appFolderId) return null;

      // Prefer newest auto snapshot in versions/
      const versions = await this.listBackupVersions();
      const newestAuto = versions.find(v => !v.manual);
      if (newestAuto) {
        const result = await this.restoreBackupVersion(newestAuto.id);
        this.notifyListeners('previous_backup_restored', {
          success: result.success,
          result: result.result,
          error: result.error,
        });
        return result;
      }

      // Legacy fallback — single logislip_backup.previous.json slot
      const meta = this.getSyncMetadata();
      let prevId = this.previousBackupFileId || meta.previousBackupFileId;
      if (!prevId) {
        const q = `name='${SYNC_CONFIG.BACKUP_PREVIOUS_FILE_NAME}' and '${this.appFolderId}' in parents and trashed=false`;
        const url = `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(q)}&fields=files(id)&pageSize=1`;
        const res = await fetch(url, { headers: { Authorization: `Bearer ${this.accessToken}` } });
        if (!res.ok) return null;
        const json = await res.json();
        if (!json.files || json.files.length === 0) return null;
        prevId = json.files[0].id;
        this.previousBackupFileId = prevId;
      }
      const dlUrl = `https://www.googleapis.com/drive/v3/files/${prevId}?alt=media`;
      const dlRes = await fetch(dlUrl, { headers: { Authorization: `Bearer ${this.accessToken}` } });
      if (!dlRes.ok) return null;
      const data = await dlRes.json();
      const result = await this.applyCloudDataToLocal(data, 'force');
      if (result && result.applied) {
        this.updateSyncMetadata({ lastCloudModifiedTime: null, lastSyncHash: null });
        for (const key of SYNC_KEYS) this.dirtyKeys.add(key);
        this.persistSyncStateSoon();
      }
      this.notifyListeners('previous_backup_restored', { success: true, result });
      return result;
    } catch (error) {
      secureLogger.error('Failed to restore previous backup:', error);
      this.notifyListeners('previous_backup_restored', { success: false, error: error.message });
      return null;
    }
  }
}

// Export singleton instance
export const cloudSyncService = new CloudSyncService();
export default cloudSyncService;
